/**
 * Ephemeral, client-side prefill email store.
 *
 * Used to carry a user's typed email across auth-flow navigations
 * (login -> register, login -> reset-password/request, reset-password/request
 * -> login, link-account -> reset-password/request) WITHOUT embedding it in
 * the URL as `?email=`.
 *
 * Why sessionStorage (not the SessionProvider / session JWT):
 *  - These flows run PRE-auth: there is no session object to attach a field
 *    to, and `useSession()` returns `data: null` on `/login`.
 *  - `useSession().update()` fires an extra `POST /api/auth/session`, which
 *    works against the single-SessionProvider goal of PIERRE-1863 Part A.
 *  - Email must NOT land in the session JWT cookie (sent on every same-origin
 *    request). sessionStorage auto-clears on tab close and is cleared here
 *    right after consume, so the PII has a one-navigation lifetime.
 *
 * All functions are SSR-safe (no-op / null when `window` is undefined) and
 * guarded so they never throw in private-mode / disabled-storage browsers.
 */
const PREFILL_EMAIL_KEY = 'pierre:prefill-email';
const RESET_PASSWORD_FLOW_HANDLE_KEY = 'pierre:reset-password-flow-handle';

/**
 * Run `fn` against `window.sessionStorage`, returning `null` on the server or
 * when storage is unavailable (private mode / disabled). Centralizes the
 * SSR guard + try/catch so the three callers below stay DRY.
 */
function withSessionStorage<T>(fn: (storage: Storage) => T): T | null {
  if (typeof window === 'undefined') return null;
  try {
    return fn(window.sessionStorage);
  } catch {
    return null;
  }
}

/**
 * Persist an email so the next auth page can prefill its field.
 * Client-only; no-op on the server.
 */
export function setPrefillEmail(email: string): void {
  withSessionStorage((storage) => {
    if (email) storage.setItem(PREFILL_EMAIL_KEY, email);
    else storage.removeItem(PREFILL_EMAIL_KEY);
  });
}

/**
 * Read (and by default consume) the prefilled email.
 * Returns `null` on the server, in private mode, or when nothing was stored.
 *
 * Pass `consume: false` to peek without clearing.
 */
export function getPrefillEmail(consume = true): string | null {
  return withSessionStorage((storage) => {
    const email = storage.getItem(PREFILL_EMAIL_KEY);
    if (email && consume) storage.removeItem(PREFILL_EMAIL_KEY);
    return email;
  });
}

/**
 * Remove any stored prefill email. Safe to call on the server (no-op).
 */
export function clearPrefillEmail(): void {
  withSessionStorage((storage) => storage.removeItem(PREFILL_EMAIL_KEY));
}

export function setResetPasswordFlowHandle(handle: string): void {
  withSessionStorage((storage) => {
    if (handle) storage.setItem(RESET_PASSWORD_FLOW_HANDLE_KEY, handle);
    else storage.removeItem(RESET_PASSWORD_FLOW_HANDLE_KEY);
  });
}

export function getResetPasswordFlowHandle(): string | null {
  return withSessionStorage((storage) =>
    storage.getItem(RESET_PASSWORD_FLOW_HANDLE_KEY),
  );
}

export function clearResetPasswordFlowHandle(): void {
  withSessionStorage((storage) =>
    storage.removeItem(RESET_PASSWORD_FLOW_HANDLE_KEY),
  );
}

/**
 * Resolve the email to prefill on an auth page: prefer an explicit URL
 * `?email=` (external links / email clients — backward-compat), then fall back
 * to sessionStorage (set by the previous auth page). Consumes the
 * sessionStorage value when it is used.
 */
export function resolvePrefillEmail(urlEmail: string | null): string | null {
  return urlEmail || getPrefillEmail();
}
